import * as React from 'react';
import styles from './LootboxRewardItemCard.scss';
import classNames from 'classnames';
import { DivTooltip } from '@wg/wows-react-uikit';
import { CrewTypes, items, ItemTypes, PresentationStyles } from '@wg/wows-entities/const';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { getLevelFromRomanFormat, isInGameBrowser } from '~/utils/utils';
import { getShipClassIcon } from '~/components/Ship/Ship';
import { interpolate, t } from '~/utils/localization';
import Account from '~/account/Account';
import Attention from '~/components/Attention/Attention';
import CrewPoints from '~/components/CrewPoints/CrewPoints';
import CrewVoiceButton from '~/components/CrewVoiceButton/CrewVoiceButton';
import PortPreviewButton, { PortPreviewTemplate } from '../PortPreviewButton/PortPreviewButton';
import { openPortByItem } from '~/settings/port';
import { PortPreviewType } from '../Port/settings';
import { useDispatch } from 'react-redux';
import { hidePopup } from '~/Actions/ActionApp';
import store from '~/Store';
import { changeVisiblePopup } from '~/Actions/ActionApp';
import { POPUPS_NAME } from '~/components/PopupManager';

export enum RewardItemPlaces {
    POPUP = 'popup',
    LIST = 'list',
}

interface IItemCard {
    reward: ILootboxReward;
    className?: string;
    inventory?: InventoryState;
    hideInventoryWidget?: boolean;
    place?: RewardItemPlaces;
    isUniqueGroup?: boolean;
    bundleId: number;
    lootboxId: number;
    goToPortHandler?: () => void;
}

const LootboxRewardItemCard = ({ reward, className, inventory, hideInventoryWidget, place, isUniqueGroup, bundleId, goToPortHandler, lootboxId }: IItemCard) => {
    const dispatch = useDispatch();
    const { additionalData } = reward;
    const [isExistsInInventory, inventoryCount] = Account.getInventoryInfoByType(reward.type as keyof Inventory, reward.id, reward.shipId);
    const isEnabledPortPreview = isInGameBrowser && ([items.VEHICLES, items.PERMOFLAGES, items.SKIN, items.STYLE, items.MSKIN] as string[]).includes(reward.type);

    const renderContent = () => {
        switch (reward.type) {
            case items.VEHICLES: {
                const backgroundImageUrl = additionalData?.nation?.icons?.large;
                const backgroundImage = { backgroundImage: `url(${backgroundImageUrl})` };
                const shipImage = { backgroundImage: `url(${additionalData.icons.small})` };
                const shipTypeImage = { backgroundImage: `url(${getShipClassIcon(additionalData)})` };
                const shipStr = getLevelFromRomanFormat(additionalData.level) + ' ' + additionalData.title;
                const isPremium = additionalData?.isPremium || additionalData?.isSpecial;
                const crew = additionalData?.defaultCrew;
                const isShowCrew = crew && crew?.type !== CrewTypes.COMMON;
                const crewImage = { backgroundImage: `url(${crew?.icons?.default})`, backgroundPosition: 'right' };

                return (
                    <div className={styles.itemContent}>
                        <div
                            className={classNames(styles.background, {
                                [styles.withDefaultCrew]: isShowCrew,
                            })}
                            style={backgroundImage}
                        />
                        <div className={styles.shipContent}>
                            <div className={styles.content}>
                                <div className={styles.image} style={shipImage} />
                                <div className={styles.water} />
                            </div>
                            <div
                                className={classNames(styles.info, styles.ship, {
                                    [styles.isPremium]: isPremium,
                                })}
                            >
                                <span className={styles.shipTypeIcon} style={shipTypeImage} />
                                <span className={styles.shipTitle}>{shipStr}</span>
                            </div>
                        </div>
                        {crew && isShowCrew && (
                            <DivTooltip
                                className={styles.defaultCrew}
                                tooltipBody={
                                    <WoWSEntity
                                        type={ItemTypes.CREWS}
                                        id={reward.defaultCrew}
                                        presentation={{
                                            style: PresentationStyles.TOOLTIP_WIDGET,
                                            withTooltip: true,
                                        }}
                                        customisation={{
                                            points: reward.crewLevel,
                                        }}
                                    />
                                }
                            >
                                <div className={styles.defaultCrewImage} style={crewImage} />
                                <div className={styles.crewPointsWrapper}>
                                    <CrewPoints points={reward.crewLevel} className={styles.crewPoints} />
                                </div>
                            </DivTooltip>
                        )}
                    </div>
                );
            }

            case items.CREWS: {
                const backgroundImageUrl = additionalData?.nation?.icons?.large;
                const backgroundImage = { backgroundImage: `url(${backgroundImageUrl})` };
                const crewImage = { backgroundImage: `url(${additionalData.icons.default})` };

                return (
                    <div className={styles.itemContent}>
                        <div className={styles.background} style={backgroundImage} />
                        <div className={styles.content}>
                            <div className={styles.image} style={crewImage} />
                        </div>
                        <div className={classNames(styles.info, styles.crew)}>{additionalData.title}</div>
                    </div>
                );
            }

            case items.PERMOFLAGES:
            case items.SKIN:
            case items.STYLE:
            case items.MSKIN: {
                const camoImage = { backgroundImage: `url(${additionalData.icons.large})` };
                let title = additionalData.title;
                if (additionalData.ship?.title) {
                    title = interpolate(t('{camo_title} – {ship_title}'), {
                        camo_title: title,
                        ship_title: additionalData.ship.title,
                    });
                }

                return (
                    <div className={styles.itemContent} data-id={reward.id}>
                        <div className={styles.content}>
                            <div className={classNames(styles.image, styles.camo)} style={camoImage} />
                        </div>
                        <div className={classNames(styles.info, styles.camo)}>{title}</div>
                    </div>
                );
            }

            case items.MULTI_BOOST: {
                const image = { backgroundImage: `url(${additionalData?.icons.large})` };
                const levelsInfoStr = interpolate(t('Может быть установлен на корабли {level} уровня'), { level: additionalData?.restrictions?.levels?.join('-') });

                return (
                    <div className={styles.itemContent} data-id={reward.id}>
                        <div className={styles.content}>
                            <div className={classNames(styles.image, styles.camo)} style={image} />
                        </div>
                        <div className={classNames(styles.info, styles.camo)}>{levelsInfoStr}</div>
                    </div>
                );
            }

            case items.LOOTBOX: {
                const image = { backgroundImage: `url(${additionalData.icons.large})` };

                const openLootbox = () => {
                    store.dispatch(
                        changeVisiblePopup(POPUPS_NAME.ITEM_DESCRIPTION_POPUP, true, {
                            item: {
                                identifier: reward.id,
                                type: reward.type,
                            },
                            autoDescriptionSettings: {},
                        }),
                    );
                };

                return (
                    <div className={styles.itemContent} data-id={reward.id} onClick={openLootbox}>
                        <div className={styles.content}>
                            <div className={classNames(styles.image, styles[reward.type])} style={image} />
                        </div>
                        <div className={classNames(styles.info, styles[reward.type])}>{additionalData.title}</div>
                    </div>
                );
            }

            default: {
                if (!additionalData) {
                    return <></>;
                }

                const image = { backgroundImage: `url(${additionalData.icons.large})` };

                return (
                    <div className={styles.itemContent} data-id={reward.id}>
                        <div className={styles.content}>
                            <div className={classNames(styles.image, styles[reward.type])} style={image} />
                        </div>
                        <div className={classNames(styles.info, styles[reward.type])}>{additionalData.title}</div>
                    </div>
                );
            }
        }
    };

    let crewVoiceId = reward.id;
    const hasSampleVo = (reward.additionalData?.hasSampleVo || reward.additionalData?.defaultCrew?.hasSampleVo) && isInGameBrowser;
    if (reward.additionalData?.defaultCrew?.hasSampleVo) {
        crewVoiceId = reward.defaultCrew;
    }

    const isNotUniqueReward = !isUniqueGroup;
    const classesWrapper = classNames(
        styles.wrapper,
        className,
        styles[reward.type],
        {
            [styles.isExists]: !hideInventoryWidget && !isNotUniqueReward && isExistsInInventory,
            [styles.withDefaultCrew]: !!reward.defaultCrew && reward.additionalData?.defaultCrew?.type !== CrewTypes.COMMON,
            [styles.withPort]: isEnabledPortPreview,
            [styles.withVoSample]: hasSampleVo,
        },
        styles[place],
    );

    const classesAlreadyExitsWrapper = classNames(styles.alreadyExitsWrapper, {
        [styles.shadow]: isNotUniqueReward,
    });

    const classesPortPreview = classNames(styles.portPreview, {
        [styles.withVo]: hasSampleVo,
    });

    const classesVOPreview = classNames(styles.voButtonWrapper, {
        [styles.withPortPreview]: isEnabledPortPreview,
    });

    const goToPreview = () => {
        window?.tooltipProvider?.hide?.();

        dispatch(hidePopup());
        openPortByItem(
            {
                shipId: +additionalData.ship?.id,
                type: reward.type,
                identifier: reward.id,
            },
            bundleId,
            false,
            PortPreviewType.CONTAINER,
            { lootboxId },
        );
        goToPortHandler?.();
    };

    return (
        <DivTooltip
            tooltipBody={
                isEnabledPortPreview && hasSampleVo ? null : (
                    <WoWSEntity
                        type={reward.type}
                        id={reward.id}
                        presentation={{
                            style: PresentationStyles.TOOLTIP_WIDGET,
                            withTooltip: true,
                        }}
                        customisation={{
                            shipId: reward.shipId,
                            isExistsInInventory: !isNotUniqueReward && isExistsInInventory,
                            inventoryCount: isNotUniqueReward ? inventoryCount : 0,
                        }}
                    />
                )
            }
            className={classesWrapper}
        >
            {renderContent()}
            {isExistsInInventory && !hideInventoryWidget && (
                <div className={classesAlreadyExitsWrapper}>
                    <Attention label={isNotUniqueReward ? inventoryCount : t('Получено')} level={'done'} className={styles.attentionWidget} isInline />
                </div>
            )}
            {isEnabledPortPreview && (
                <div className={classesPortPreview} onClick={goToPreview}>
                    <PortPreviewButton template={PortPreviewTemplate.miniature} className={styles.portPreviewButton} />
                </div>
            )}
            {hasSampleVo && (
                <div className={classesVOPreview}>
                    <CrewVoiceButton crewId={crewVoiceId} className={styles.voButton} withoutGuideTip />
                </div>
            )}
        </DivTooltip>
    );
};

export default LootboxRewardItemCard;
